import { useState } from "react";
import { Flex, Segmented } from "antd";
import { useTranslate } from "@refinedev/core";
import { TScript } from "pages/media/types/";
import { Narration } from "../../VoiceoverNarrationCard/components/Narration";
import { Pronunciation } from "../../VoiceoverNarrationCard/components/Pronunciation";
import { Emotions } from "../../VoiceoverNarrationCard/components/Emotions";

enum ScriptEditTab {
  TEXT = "text",
  EMO = "emotions",
  PRONUNCIATION = "pronunciation",
}

// Inline editor for a single script: the editable text plus the pronunciation
// and emotions tabs — the same surface the old voiceover narration card opened
// in a modal. Changes persist as soon as they complete (text on blur, markers
// on add/remove) since there is no modal save button to batch them.
export const SlideScriptEditor = ({
  script,
  onSave,
  index,
  isRTL = false,
  language,
  textOnly = false,
  sectionId,
  voiceId,
}: {
  script: string;
  onSave: (content: string) => void;
  index: number;
  isRTL?: boolean;
  language?: string;
  // Whole-script view: plain narration text only, no pronunciation/emotions
  // tabs — those stay specific to the single current-slide script editor.
  textOnly?: boolean;
  sectionId?: string;
  voiceId?: string;
}) => {
  const t = useTranslate();
  const [selectedTab, setSelectedTab] = useState<ScriptEditTab>(
    ScriptEditTab.TEXT
  );

  const textEditor = (
    <Narration
      script={script}
      isEditable
      index={index}
      onEditComplete={(_: number, updatedScript: Partial<TScript>) => {
        // Empty, whitespace-only or non-string content persists as an empty
        // script.
        onSave(
          typeof updatedScript.content === "string" &&
            updatedScript.content.trim() !== ""
            ? updatedScript.content
            : ""
        );
      }}
      isRTL={isRTL}
    />
  );

  if (textOnly) return textEditor;

  return (
    <Flex vertical gap={16}>
      <Segmented<ScriptEditTab>
        options={Object.values(ScriptEditTab).map((tab) => ({
          label: t(
            `components.templates.templatePreview.voiceoverNarration.editModal.tabs.${tab}`
          ),
          value: tab,
        }))}
        value={selectedTab}
        onChange={(value) => setSelectedTab(value)}
        style={{ width: "fit-content" }}
      />
      {selectedTab === ScriptEditTab.TEXT && textEditor}
      {selectedTab === ScriptEditTab.PRONUNCIATION && (
        <Pronunciation
          script={script}
          onChange={onSave}
          isRTL={isRTL}
          language={language}
          sectionId={sectionId}
          voiceId={voiceId}
        />
      )}
      {selectedTab === ScriptEditTab.EMO && (
        <Emotions
          script={script}
          onChange={onSave}
          isRTL={isRTL}
          language={language}
        />
      )}
    </Flex>
  );
};
